import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { api } from 'helpers/axios';
import { setUserCookie, clearCookie } from 'helpers/cookie';
import { apiBaseUrl } from 'helpers/runtimeConfig';
import * as Sentry from "@sentry/react";
import { errorMessage } from 'helpers/common';
import { hasAnyPermissionWithin } from 'helpers/permission';
import { LOB_MODULE } from '../constants';
import { getStoredModule, isDbModule, normalizeModules, setStoredModule } from 'helpers/module';

const fetchUser = createAsyncThunk('fetchUser', async (_, thunkAPI) => {
  try {
    const { data, status } = await api.get('/auth/me');
    if (status === 200) return data;
  } catch (err) {
    Sentry.captureException(err);
    return thunkAPI.rejectWithValue(errorMessage(err, 'fetch user'));
  }
});

const fetchMenus = createAsyncThunk('fetchMenus', async (_, thunkAPI) => {
  const { user } = thunkAPI.getState();
  try {
    const { data, status } = await api.get('/menus');
    if (status === 200) {
      const newData = { data: [] };
      for (const menu of data.data) {
        if (menu.submenus.data.length > 0) {
          const newSubMenusData = [];
          for (const sub of menu.submenus.data) {
            if (hasAnyPermissionWithin(sub.permission, user.permissions)) {
              if (sub.path.startsWith('/evaluation/') && sub.path !== '/evaluation/all-evaluations') {
                if (isDbModule(user.module)) {
                  if ((sub.path === '/evaluation/evaluator-overview' && user?.groups_evaluating?.length > 0) || (sub.path === '/evaluation/secondary-evaluator-overview' && user?.groups_secondary_evaluating?.length > 0)) {
                    newSubMenusData.push(sub);
                  }
                } else {
                  if ((['/evaluation/evaluator-overview', '/evaluation/team-objectives-overview'].includes(sub.path) && user?.groups_evaluating?.length > 0) || (sub.path === '/evaluation/review' && user?.groups_reviewing?.length > 0)) {
                    newSubMenusData.push(sub);
                  }
                }
              } else {
                newSubMenusData.push(sub);
              }
            }
          }
          if (newSubMenusData.length > 0) {
            menu.submenus.data = newSubMenusData;
            newData.data.push(menu);
            continue;
          }
        } else if (hasAnyPermissionWithin(menu.permission, user.permissions)) {
          newData.data.push(menu);
        }
      }
      if (newData.data.length < 1) newData.data = null;
      return newData;
    }
  } catch (err) {
    Sentry.captureException(err);
    return thunkAPI.rejectWithValue(errorMessage(err, 'fetch menus'));
  }
});

const fetchTranslation = createAsyncThunk('fetchTranslation', async (reFetch = false, thunkAPI) => {
  const cached = localStorage.getItem('translation.cache');
  const useCachedTranslation = process.env.REACT_APP_ENV !== 'local';
  if (cached && !reFetch && useCachedTranslation) {
    return JSON.parse(cached);
  }

  try {
    const { data } = await api.get('/common/translations');
    // An empty body used to fall through and fulfil the thunk with `undefined`,
    // which the reducer then dereferenced.
    if (!data || Object.keys(data).length < 1) {
      return thunkAPI.rejectWithValue(errorMessage('Empty translation response', 'fetch translation'));
    }

    // separate translation into global, tooltips, and dropdowns
    // tooltips have `tooltips` prefix, dropdowns have `dropdowns` prefix
    const translations = { global: {}, tooltips: {}, dropdowns: {} };

    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith('tooltips.')) {
        translations.tooltips[key] = value;
      } else if (key.startsWith('dropdowns.')) {
        translations.dropdowns[key] = value;
      } else {
        translations.global[key] = value;
      }
    }

    localStorage.setItem('translation.cache', JSON.stringify(translations));

    return translations;
  } catch (err) {
    Sentry.captureException(err);
    return thunkAPI.rejectWithValue(errorMessage(err, 'fetch translation'));
  }
});

const saveUser = createAsyncThunk('saveUser', async (formData, thunkAPI) => {
  try {
    const { data, status } = await api.patch('/users', formData);
    if (status === 200) return data;
  } catch (err) {
    Sentry.captureException(err);
    return thunkAPI.rejectWithValue(errorMessage(err, 'save preferred language'));
  }
});

const login = createAsyncThunk('login', async ({ email, password, account_id }, thunkAPI) => {
  try {
    api.defaults.headers['X-Tenant'] = localStorage.getItem('accountId');
    const { status } = await api.post('/login', { email, password }, { baseURL: apiBaseUrl });
    if (status === 200) return true;
  } catch (err) {
    Sentry.captureException(err);
    return thunkAPI.rejectWithValue(errorMessage(err, 'log in'));
  }
});

const logout = createAsyncThunk('logout', async (reason = null, thunkAPI) => {
  await api.post('/logout', {
    reason: reason || 'User initiated logout'
  }, { baseURL: apiBaseUrl });

  return true;
});

const getCurrentModule = (availableModules) => {
  // Use the only available module, otherwise restore the user's last selection.
  return availableModules.length === 1 ? availableModules[0] :
    getStoredModule(LOB_MODULE);
}

const internalTenantMatchers = (process.env.REACT_APP_INTERNAL_TENANTS || 'kommsolutions')
  .split(',')
  .map((value) => value.trim().toLowerCase())
  .filter(Boolean);

const isInternalTenant = (identifier = '') => {
  const normalizedIdentifier = `${identifier}`.toLowerCase();

  return process.env.REACT_APP_ENV === 'local'
    || internalTenantMatchers.some((value) => normalizedIdentifier.includes(value));
};

export const userSlice = createSlice({
  name: 'user',
  initialState: {
    name: '',
    hash_id: '',
    menus: [],
    permissions: [],
    roles: [],
    groups_evaluating: [],
    groups_secondary_evaluating: [],
    groups_reviewing: [],
    lang: 'de',
    display_per_page: 0,
    show_release_notes: false,
    last_seen_version: '',
    translation: {
      global: {},
      tooltips: {},
      dropdowns: {},
    },
    tenant: {
      identifier: '',
      id: '',
      is_shadow: false,
      is_internal: false
    },
    module: LOB_MODULE,
    available_modules: [],
    debug_translation: false,
    help_edit_mode: false,
    show_lk_badge: (localStorage.getItem('show_lk_badge') === 'true'),
    seed_required_data_mode: false,
    error: null,
    is_dirty: false,
    rps: false,
    session_timeout: 60,
    impersonated_by: null,
  },
  reducers: {
    updateError: (state, action) => {
      state.error = action.payload;
    },
    toggleDebugTranslation: (state, action) => {
      state.debug_translation = !state.debug_translation;
    },
    toggleShowLkBadge: (state, action) => {
      state.show_lk_badge = !state.show_lk_badge;
      localStorage.setItem('show_lk_badge', state.show_lk_badge);
    },
    toggleHelpEditMode: (state, action) => {
      state.help_edit_mode = !state.help_edit_mode;
    },
    toogleSeedRequiredDataMode: (state, action) => {
      state.seed_required_data_mode = !state.seed_required_data_mode;
    },
    setIsDirty: (state, action) => {
      if (state.is_dirty !== action?.payload) {
        state.is_dirty = action.payload;
      }
    },
    updateModule: (state, action) => {
      state.module = setStoredModule(action.payload);
    },
    resetModule: (state, action) => {
      const currentModule = getCurrentModule(state.available_modules);
      state.module = setStoredModule(currentModule);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.name = `${action.payload.first_name} ${action.payload.last_name}`;
        state.hash_id = action.payload.hash_id;
        state.permissions = action.payload.permissions;
        state.roles = action.payload.roles;
        state.groups_evaluating = action.payload.groups_evaluating;
        state.groups_secondary_evaluating = action.payload.groups_secondary_evaluating;
        state.groups_reviewing = action.payload.groups_reviewing;
        state.tenant.identifier = action.payload.tenant.identifier;
        state.tenant.is_shadow = action.payload.tenant.identifier.split('-').pop() === 'shadow';
        state.tenant.is_internal = isInternalTenant(action.payload.tenant.identifier);
        state.lang = action.payload.preferences.preferred_lang || 'de';
        localStorage.setItem('preferred_lang', state.lang);
        state.rps = action.payload.rps || false;
        state.display_per_page = action.payload.preferences.display_per_page || 20;
        if (action.payload.preferences.hasOwnProperty('show_release_notes')) {
          state.show_release_notes = JSON.parse(action.payload.preferences.show_release_notes);
        }
        state.last_seen_version = action.payload.preferences?.last_seen_version || '';
        state.session_timeout = action.payload.session_timeout || 60;
        state.impersonated_by = action.payload.meta?.impersonated_by ?? null;
        state.error = null;
        Sentry.setUser({
          id: action.payload.hash_id
        });
        Sentry.setContext('tenant', {
          identifier: action.payload.tenant.identifier,
          id: action.payload.tenant.id
        })

        const availableModules = normalizeModules(
          action.payload.meta.available_modules || action.payload.meta.mode_switches,
          [LOB_MODULE]
        );
        state.available_modules = availableModules;

        const currentModule = getCurrentModule(availableModules);
        state.module = setStoredModule(currentModule);
      })
      .addCase(fetchMenus.fulfilled, (state, action) => {
        state.menus = action.payload.data;
      })
      .addCase(fetchTranslation.fulfilled, (state, action) => {
        state.translation.global = action.payload.global;
        state.translation.tooltips = action.payload.tooltips;
        state.translation.dropdowns = action.payload.dropdowns;
      })
      .addCase(saveUser.fulfilled, (state, action) => {
        state.lang = action.payload.preferences.preferred_lang || 'de';
        localStorage.setItem('preferred_lang', state.lang);
        state.display_per_page = action.payload.preferences.display_per_page || 20;
        if (action.payload.preferences.hasOwnProperty('show_release_notes')) {
          state.show_release_notes = JSON.parse(action.payload.preferences.show_release_notes);
        }
        state.last_seen_version = action.payload.preferences?.last_seen_version || '';
      })
      .addCase(login.fulfilled, (state, action) => {
        setUserCookie();
      })
      .addCase(logout.fulfilled, (state, action) => {
        clearCookie();
      })
      .addCase(logout.rejected, (state, action) => {
        clearCookie();
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const getUserName = (state) => state.user.name;
export const getUserHashId = (state) => state.user.hash_id;
export const getTenant = (state) => state.user.tenant.identifier;
export const getMenus = (state) => state.user.menus;
export const getPermissions = (state) => state.user.permissions;
export const getRoles = (state) => state.user.roles;
export const getGroupsEvaluating = (state) => state.user.groups_evaluating;
export const getGroupsSecondaryEvaluating = (state) => state.user.groups_secondary_evaluating;
export const getGroupsReviewing = (state) => state.user.groups_reviewing;
export const getLang = (state) => state.user.lang;
export const getDisplayPerPage = (state) => state.user.display_per_page;
export const getShowReleaseNotes = (state) => state.user.show_release_notes;
export const getLastSeenVersion = (state) => state.user.last_seen_version;
export const getTranslation = (state) => state.user.translation;
export const getGlobalTranslation = (state) => state.user.translation.global;
export const getTooltipsTranslation = (state) => state.user.translation.tooltips;
export const getDropdownsTranslation = (state) => state.user.translation.dropdowns;
export const getDebugTranslation = (state) => state.user.debug_translation;
export const getHelpEditMode = (state) => state.user.help_edit_mode;
export const getShowLkBadge = (state) => state.user.show_lk_badge;
export const getSeedRequiredDataMode = (state) => state.user.seed_required_data_mode;
export const getUserError = (state) => state.user.error;
export const getTenantIsShadow = (state) => state.user.tenant.is_shadow;
export const getTenantIsInternal = (state) => state.user.tenant.is_internal;
export const getIsDirty = (state) => state.user.is_dirty;
export const getSessionTimeout = (state) => state.user.session_timeout;
export const getModule = (state) => state.user.module;
export const getImpersonatedBy = (state) => state.user.impersonated_by;
export const getAvailableModules = (state) => state.user.available_modules;

export { fetchUser, fetchMenus, fetchTranslation, saveUser, login, logout };

export const { updateError, toggleDebugTranslation, toggleHelpEditMode, toggleShowLkBadge, toogleSeedRequiredDataMode, setIsDirty, updateModule, resetModule } = userSlice.actions;

export default userSlice.reducer;
